import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel, Hidden,
  InputAdornment,
  Link,
  Paper,
  Typography,
} from '@material-ui/core';
import {GlobalConfig, LocalStorageKeys, QueryParam} from 'common/types';
import {APIError, APIResponseErrorType} from 'common/types';
import ButtonWithProgress from 'components/ButtonWithProgress';
import FFTextField from 'components/form/FFTextField';
import Logo from 'components/Logo';
import {makeTrackable} from 'modules/ga';
import {createI18nErrorMessage} from 'modules/intl';
import {useSnackbar} from 'notistack';
import * as R from 'ramda';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Field, Form} from 'react-final-form';
import {Helmet} from 'react-helmet';
import {FormattedHTMLMessage, FormattedMessage, useIntl} from 'react-intl';
import {connect, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import FadedCollapse from 'screens/common/FadedCollapse';
import LocaleSwitcher from 'screens/common/LocaleSwitcher';
import MaskedPhoneInput, {
  getLocalPhoneNumber,
  normalizePhoneNumber,
  parseMaskedPhoneNumber,
  UKRAINE_COUNTRY_CODE,
} from 'screens/common/MaskedPhoneInput';
import RouterLink from 'screens/common/RouterLink';
import VerificationCodeInputWithCountdown from 'screens/common/VerificationCodeInputWithCountdown';
import {AppState} from 'store';
import * as UserActions from 'store/user/actions';
import * as UserSelectors from 'store/user/selectors';
import * as GlobalConfigActions from 'store/global/actions';
import * as GlobalConfigSelects from 'store/global/selectors';
import {createPageTitle, getSeoTitle} from 'utils';
import {
  createLegacyInterfaceUrl,
  createLoginLink,
  createRegistrationInfoLink, createRestoreUserLink,
  extractAuxiliaryQueryParams,
  parseLocationSearch,
} from 'utils/navigation';
import {CommonValidators, composeValidators} from 'utils/validation';
import UserAvatar from '../../screens/common/UserAvatar/index';
import cx from 'classnames';
import useStyles from './styles';
import {deleteFromStorage, writeStorage} from '@rehooks/local-storage';
import {SUPPORT_VIBER_CHAT} from 'common/constants';
import LoginRegistrationInfo from 'screens/common/LoginRegistrationInfo'
import ReferralRegistrationInfo from "../common/ReferralRegistrationInfo";
import {seoPatternsSelector} from 'store/global/selectors';


const TrackableMuiLink = makeTrackable(Link);
const siteKey = process.env.REACT_APP_RECAPTCHA_KEY;

const TRANSITION_COLLAPSE_PROPS = {
  mountOnEnter: true,
  unmountOnExit: true,
};

const TrackableButtonBase = makeTrackable(ButtonBase);
const TrackableButtonWithProgress = makeTrackable(ButtonWithProgress);
const TrackableButton = makeTrackable(Button);
const TrackableLink = makeTrackable(Link);

const mapStateToProps = (state: AppState) => ({
  isCodeSending: UserSelectors.isRegistrationCodeSending(state),
  isCodeSent: UserSelectors.isRegistrationCodeSent(state),
  sendCodeError: UserSelectors.sendRegistrationCodeError(state),

  isCodeVerifying: UserSelectors.isRegistrationCodeVerifying(state),
  isCodeVerified: UserSelectors.isRegistrationCodeVerified(state),
  codeVerificationError: UserSelectors.registrationCodeVerificationError(state),

  registerId: UserSelectors.registerId(state),
  referralInfo: UserSelectors.referralInfo(state),
  globalConfig: GlobalConfigSelects.globalConfigSelector(state),
});

interface RegistrationScreenProps
  extends ReturnType<typeof mapStateToProps>,
    RouteComponentProps {
  getReferralInfo: (
    ...args: Parameters<typeof UserActions.getReferralInfo>
  ) => void;
  sendVerificationCode: (
    ...args: Parameters<typeof UserActions.sendRegistrationCode>
  ) => void;
  verifyCode: (
    ...args: Parameters<typeof UserActions.verifyRegistrationCode>
  ) => void;
  resetRegistrationState: (
    ...args: Parameters<typeof UserActions.resetRegistrationState>
  ) => void;
  fetchGlobalConfig: () => void;
  userLogout: () => void;
}

enum Stage {
  Phone = 'Phone',
  VerificationCode = 'VerificationCode',
  LoginSuggestion = 'LoginSuggestion',
  RestoreUser = 'RestoreUser',
}

enum RegistrationFormFields {
  Phone = 'phone',
  TermsAccepted = 'termsAccepted',
}

interface RegistrationFormValues {
  phone: string;
  termsAccepted: boolean;
}

const RegistrationScreen: FunctionComponent<RegistrationScreenProps> = ({
  location,
  isCodeSending,
  isCodeSent,
  sendVerificationCode,
  sendCodeError,
  verifyCode,
  codeVerificationError,
  isCodeVerifying,
  isCodeVerified,
  history,
  registerId,
  resetRegistrationState,
  getReferralInfo,
  referralInfo,
  globalConfig,
  userLogout
}: RegistrationScreenProps) => {
  const checkRecaptcha = (
    func: (phone: string, token: string) => void,
    phone: string
  ) => {
    if (typeof grecaptcha !== 'undefined') {
      grecaptcha.execute(siteKey, {action: 'register'}).then((token) => {
        func(phone, token);
      });
    }
  };
  useEffect(() => resetRegistrationState, [resetRegistrationState]);

  const intl = useIntl();
  const {formatMessage: fm} = intl;
  const classes = useStyles();

  const _snackbar = useSnackbar();
  // eslint-disable-next-line
  const snackbar = useMemo(() => _snackbar, []);

  const createErrorMessage = useCallback(createI18nErrorMessage(intl), [intl]);

  const queryParams = useMemo(
    () => parseLocationSearch(location.search),
    [location]
  );

  const auxiliaryQueryParams = useMemo(
    () => extractAuxiliaryQueryParams(queryParams),
    [queryParams]
  );

  const utmParams = useMemo(
    () => Object.fromEntries(Object.entries(queryParams).filter(([key]) => key.indexOf('utm') === 0)),
    [queryParams]
  );

  const initialFormValues = useMemo(
    () => ({
      [RegistrationFormFields.Phone]: queryParams[QueryParam.Phone],
      [RegistrationFormFields.TermsAccepted]: false,
    }),
    [queryParams]
  );

  const {[QueryParam.Referral]: referral} = queryParams;

  const [currentStage, setCurrentStage] = useState(Stage.Phone);
  const [lastNormalizedPhone, setLastNormalizedPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [hasReferralInfo, setHasReferralInfo] = useState(false);

  const referralRegistrationActive = globalConfig?.referralProgram?.active;
  const seoPatterns = useSelector(seoPatternsSelector);


  const isCurrentStage = (stage: Stage) => currentStage === stage;

  const handlePhoneFormSubmit = useCallback(
    (values: RegistrationFormValues) => {
      const normalizedPhone = normalizePhoneNumber(values.phone);

      setLastNormalizedPhone(normalizedPhone);

      checkRecaptcha(sendVerificationCode, normalizedPhone);
    },
    [sendVerificationCode]
  );

  const handlePhoneFieldChange = useCallback(() => {
    if (currentStage !== Stage.Phone) {
      setCurrentStage(Stage.Phone);
    }
  }, [currentStage]);

  useEffect(() => {
    if(!R.isEmpty(utmParams)){
      writeStorage(LocalStorageKeys.RegisterUtmParams, utmParams);
    }
  }, []);

  useEffect(() => {
    if (!hasReferralInfo) {
      if (referral && referralRegistrationActive) {
        setHasReferralInfo(true);
        getReferralInfo(referral);
      }
    }
  }, [hasReferralInfo, referralRegistrationActive]);

  useEffect(() => {
    if (isCodeSent) {
      userLogout();
      setCurrentStage(Stage.VerificationCode);
    }
  }, [isCodeSent]);

  useEffect(() => {
    if (sendCodeError) {
      if (
        sendCodeError instanceof APIError &&
        sendCodeError.errorType === APIResponseErrorType.PhoneAlreadyRegistered
      ) {
        setCurrentStage(Stage.LoginSuggestion);
      } else if (
        sendCodeError instanceof APIError &&
        sendCodeError.errorType === APIResponseErrorType.UserDeleted
      ) {
        setCurrentStage(Stage.RestoreUser);
      } else {
        snackbar.enqueueSnackbar(createErrorMessage(sendCodeError), {
          key: 'send_code_error',
        });
      }
    }
  }, [sendCodeError, snackbar, createErrorMessage]);

  useEffect(() => {
    if (codeVerificationError) {
      snackbar.enqueueSnackbar(createErrorMessage(codeVerificationError), {
        key: 'verify_code_error',
      });
    }
  }, [codeVerificationError, snackbar, createErrorMessage]);

  useEffect(() => {
    if (isCodeSent) {
      snackbar.enqueueSnackbar(
        fm({id: 'registration_screen.notification.code.sent'}),
        {
          key: 'code_sent',
        }
      );
    }
  }, [isCodeSent, snackbar, fm]);

  useEffect(() => {
    if (isCodeVerified) {
      // Must remove old registration data, when user start flow from beginning
      deleteFromStorage(LocalStorageKeys.Registration);

      history.push(
        createRegistrationInfoLink({
          [QueryParam.RegisterId]: registerId,
          ...auxiliaryQueryParams,
        })
      );
    }
  }, [isCodeVerified, history, registerId, auxiliaryQueryParams]);

  const handleVerificationCodeInputChange = useCallback((code: string) => {
    setVerificationCode(code);
  }, []);

  const handleVerificationCodeInputComplete = useCallback(
    (code: string) => {
      if (lastNormalizedPhone) {
        verifyCode(lastNormalizedPhone, code);
      }
    },
    [lastNormalizedPhone, verifyCode]
  );

  const handleVerificationCodeDidNotReceiveClick = useCallback(() => {
    if (lastNormalizedPhone) {
      checkRecaptcha(sendVerificationCode, lastNormalizedPhone);
    }
  }, [lastNormalizedPhone, sendVerificationCode]);

  return (
    <>
      <Helmet>
        <title>
          {createPageTitle(getSeoTitle(seoPatterns || [], '/registration') || fm({id: 'registration_screen.html_title'}))}
        </title>
      </Helmet>
      <Box
        className={classes.root}
        bgcolor="primary.main"
        display="flex"
        flexDirection="column"
      >
        <Box display="flex" justifyContent="flex-end" p={{xs: 1, sm: 1, md: 1, lg: 4, xl: 4}} >

        </Box>
        <Box
          maxWidth={836}
          mx={1}
          justifySelf="center"
          alignSelf="center"
          flex={1}
        >
        <Box className={classes.formLogoWr}>
            <Logo logoType="big" />
        </Box>
          <Box mb={3} mt={1} className={classes.formParent}>
            <Paper elevation={1}>
              <Box className={classes.formWrap}>

                {(!referral || !referralRegistrationActive) && <LoginRegistrationInfo title={fm({id: 'registration_screen.info.title'})} subtitle={fm({id: 'registration_screen.info.text'})}/>}
                {referral && referralRegistrationActive && <ReferralRegistrationInfo referral={referral} referralInfo={referralInfo} bonus={globalConfig?.referralProgram?.registerBonus}/>}
                <Box className={classes.formCont}
                  pl={3.8}
                  position="relative"
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" className={classes.formTitle}>
                      {fm({id: 'registration_screen.form.title'})}
                    </Typography>

                    <Box>
                      <TrackableButtonBase
                          gaEvent={{
                        category: 'registration_form',
                        action: 'login',
                      }}
                          // @ts-ignore
                          component={RouterLink}
                          to={createLoginLink({})}
                      >
                        <Typography color="textSecondary" className={classes.formLoginBtn}>
                          {fm({id: 'login_screen.login'})}
                        </Typography>
                      </TrackableButtonBase>
                    </Box>

                  </Box>
                  <Form
                    onSubmit={handlePhoneFormSubmit}
                    initialValues={initialFormValues}
                  >
                    {({handleSubmit, values}) => (
                      <form onSubmit={handleSubmit} id={'registrationForm'}>
                        <Box mt={3} mb={0} className={classes.formWr}>
                          <Field
                            name={RegistrationFormFields.Phone}
                            validate={composeValidators([
                              CommonValidators.required,
                              R.compose(
                                CommonValidators.phone,
                                parseMaskedPhoneNumber
                              ),
                            ])}
                            component={FFTextField}
                            InnerInputProps={{
                              label: fm({id: 'form_fields.phone.title'}),
                              fullWidth: true,
                              variant: 'outlined',
                              InputProps: {
                                placeholder: fm({
                                  id: 'form_fields.phone.placeholder',
                                }),
                                inputComponent: MaskedPhoneInput as any,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    +{UKRAINE_COUNTRY_CODE}
                                  </InputAdornment>
                                ),
                              },
                              onChange: handlePhoneFieldChange,
                            }}
                          />
                        </Box>

                        <FadedCollapse in={isCurrentStage(Stage.Phone)}>
                          <>
                            <Box my={1}>
                              <Field
                                type="checkbox"
                                name={RegistrationFormFields.TermsAccepted}
                              >
                                {({input: {value, onChange}}) => (
                                  <FormControlLabel
                                    classes={{
                                      root: classes.formControlRoot,
                                      label: classes.formControlLabel,
                                    }}
                                    control={
                                      <Checkbox
                                        value={!!value}
                                        onChange={onChange}
                                        color="primary"
                                      />
                                    }
                                    label={fm(
                                      {
                                        id: 'registration_screen.form.terms.message',
                                      },
                                      {
                                        link: (
                                          <TrackableLink
                                            href={createLegacyInterfaceUrl(
                                              '/pages/terms/'
                                            )}
                                            key="terms_link"
                                            color="textPrimary"
                                            underline="always"
                                            gaEvent={{
                                              category: 'registration_form',
                                              action: 'terms_link',
                                            }}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <FormattedMessage id="registration_screen.form.terms.link" />
                                          </TrackableLink>
                                        ),
                                      }
                                    )}
                                  />
                                )}
                              </Field>
                            </Box>
                            <TrackableButtonWithProgress
                              variant="contained"
                              color="primary"
                              fullWidth
                              size="large"
                              type="submit"
                              isFetching={isCodeSending}
                              disabled={
                                !values[RegistrationFormFields.TermsAccepted]
                              }
                              gaEvent={{
                                category: 'registration_form',
                                action: 'submit',
                              }}
                            >
                              {fm({id: 'registration_screen.form.submit'})}
                            </TrackableButtonWithProgress>
                          </>
                        </FadedCollapse>

                        <FadedCollapse
                          in={isCurrentStage(Stage.LoginSuggestion)}
                        >
                          <Box>
                            <Box my={1}>
                              <Typography align="center">
                                {fm({
                                  id: 'registration_screen.form.already_registered',
                                })}
                              </Typography>
                            </Box>

                            <TrackableButton
                              variant="contained"
                              color="primary"
                              fullWidth
                              size="large"
                              // @ts-ignore
                              component={RouterLink}
                              to={createLoginLink({
                                [QueryParam.Phone]:
                                  getLocalPhoneNumber(lastNormalizedPhone),
                                ...auxiliaryQueryParams,
                              })}
                            >
                              {fm({id: 'registration_screen.form.login'})}
                            </TrackableButton>
                          </Box>
                        </FadedCollapse>

                        <FadedCollapse in={isCurrentStage(Stage.RestoreUser)}>
                          <>
                            <Box mb={2} mt={2}>
                              <Typography align="center">
                                {fm({id: 'api_errors.user_deleted'})}
                              </Typography>
                            </Box>

                            <TrackableButton
                              variant="contained"
                              color="primary"
                              fullWidth
                              size="large"
                              // @ts-ignore
                              component={RouterLink}
                              gaEvent={{
                                category: 'login_form',
                                action: 'restore_user',
                              }}
                              to={createRestoreUserLink({
                                [QueryParam.Phone]: getLocalPhoneNumber(
                                  lastNormalizedPhone
                                ),
                                ...auxiliaryQueryParams,
                              })}
                            >
                              {fm({id: 'login_screen.restore_user'})}
                            </TrackableButton>
                          </>
                        </FadedCollapse>

                        <FadedCollapse
                          in={isCurrentStage(Stage.VerificationCode)}
                          CollapseProps={TRANSITION_COLLAPSE_PROPS}
                        >
                          <Box mt={4}>
                            <VerificationCodeInputWithCountdown
                              id="registration_form"
                              value={verificationCode}
                              onChange={handleVerificationCodeInputChange}
                              isFetching={isCodeVerifying}
                              onComplete={handleVerificationCodeInputComplete}
                              focused={isCurrentStage(Stage.VerificationCode)}
                              onDidNotReceiveClick={
                                handleVerificationCodeDidNotReceiveClick
                              }
                            />
                          </Box>
                        </FadedCollapse>
                      </form>
                    )}
                  </Form>

                  <img className={classes.infoImg}
                       src={require('./icons/form_img_x1.png')}
                       srcSet={`${require('./icons/form_img_x1.png')} 1x, ${require('./icons/form_img_x2.png')} 2x`}
                  />

                </Box>
              </Box>
            </Paper>
          </Box>

          <Box
            display="flex" justifyContent="center" mb="2"
            >
            <TrackableMuiLink
                className={classes.regCallBack}
                href={SUPPORT_VIBER_CHAT}
                color="inherit"
                gaEvent={{
                        category: 'footer',
                        action: 'support_viber',
                      }}
            >
              {fm({id: 'registration_screen.call_back'})}
            </TrackableMuiLink>
          </Box>


        <img className={classes.infoImgMob}
             src={require('./icons/form_img_x1.png')}
             srcSet={`${require('./icons/form_img_x1.png')} 1x, ${require('./icons/form_img_x2.png')} 2x`}
        />


        </Box>
      </Box>
    </>
  );
};

const ConnectedRegistrationScreen = connect(mapStateToProps, {
  getReferralInfo: UserActions.getReferralInfo,
  sendVerificationCode: UserActions.sendRegistrationCode,
  verifyCode: UserActions.verifyRegistrationCode,
  resetRegistrationState: UserActions.resetRegistrationState,
  fetchGlobalConfig: GlobalConfigActions.fetchGlobalConfig,
  userLogout: UserActions.logout,
})(RegistrationScreen);

export default ConnectedRegistrationScreen;
